import '.cq{margin-bottom:4.5rem;}.ssr-plugin'
import Row from '@app/components/layout/Row'
import { InitiativeCreatePageLayout } from '@app/pages/layout/InitiativeCreatePageLayout'
import { PhaseSelectionStep } from './steps/PhaseSelectionStep'
import { useCallback, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import { Initiative } from '@schema/initiative'
import { PhaseType } from '@schema/phase'
import { MainDetailsStep } from './steps/MainDetailsStep'
import {
  creationWorkflow,
  InitiativeCreationStep,
  initiativeCreationSteps,
} from './blocks/workflow'
import { useTranslations } from '@i18n/useLanguage'
import {
  faArrowLeft,
  faArrowRight,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { useAuthState, useClient } from '@based/sdk/react'
import { Modal } from '@app/components/blocks/modal/Modal'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { Text } from '@app/components/core/typography/Text'
import { LivingParametersStep } from './steps/LivingParametersStep'
import { LoaderWrapper } from '@app/components/blocks/loader/LoaderWrapper'
import { MembersParametersStep } from './steps/MembersParametersStep'
import { ContactDetailsStep } from './steps/ContactDetailsStep'
import { CallScheduleStep } from './steps/CallScheduleStep'
import { FinishStep } from './steps/FinishStep'
import { LegalInfoStep } from './steps/LegalInfoStep'
import { useToast } from '@app/util/hooks/useToast'
import { styled } from 'inlines'
import { AmenitiesStep } from './steps/AmenitiesStep'

const steps = initiativeCreationSteps

export const InitiativeCreation = ({
  initiativeId,
  currentStep = steps.phaseSelection,
}: {
  initiativeId?: string
  currentStep?: string
}) => {
  const isEditing = !!initiativeId
  const stepValues = Object.values(steps)
  const step = Object.keys(steps).includes(currentStep)
    ? (currentStep as InitiativeCreationStep)
    : steps.phaseSelection
  const client = useClient()
  const authState = useAuthState()
  const [_, navigate] = useLocation()
  const translations = useTranslations().app.dashboard.initiative.creation
  const isMobile = useMediaQuery()
  const { showFailToast } = useToast()

  const [completedSteps, setCompletedSteps] = useState<
    Set<InitiativeCreationStep>
  >(
    new Set(
      step === steps.phaseSelection
        ? []
        : stepValues.slice(0, stepValues.indexOf(step)),
    ),
  )
  const [phase, setPhase] = useState<{ id: number; type: PhaseType }>(null)
  const [newInitiativeId, setNewInitiativeId] = useState<string>(initiativeId)
  const [initiative, setInitiative] = useState<Partial<Initiative>>(null)
  const [exitConfirmationOpen, setExitConfirmationOpen] = useState(false)

  const setStep = useCallback(
    (step: InitiativeCreationStep) => {
      navigate(pages.dashboardOnboardInitiative(newInitiativeId, step))
    },
    [newInitiativeId, navigate],
  )

  const currentWorkflow = useCallback(
    () =>
      creationWorkflow[phase?.type] ?? [initiativeCreationSteps.phaseSelection],
    [phase],
  )

  const isNextStepEnabled = useCallback(() => {
    return completedSteps.has(step) || step === steps.finish
  }, [completedSteps, step])

  const isPreviousVisible = useCallback(() => {
    if (!phase) {
      return false
    }

    const workflow = currentWorkflow()
    const stepIndex = workflow.indexOf(step)

    if (stepIndex === -1) {
      throw new Error('Workflow error')
    }

    if (stepIndex === 0) {
      return false
    }

    return true
  }, [step, phase, currentWorkflow])

  const nextStepCallback = useCallback(() => {
    const workflow = currentWorkflow()
    const stepIndex = workflow.indexOf(step)

    if (stepIndex === -1) {
      throw new Error('Workflow error')
    }

    if (step === steps.finish) {
      navigate(pages.dashboardViewInitiative(newInitiativeId))
      return
    }

    if (step === steps.contactDetails && initiative?.isCalendlyScheduled) {
      setStep(steps.finish)
      return
    }

    const nextStep = workflow[stepIndex + 1]
    setStep(nextStep)
  }, [
    step,
    initiative?.isCalendlyScheduled,
    newInitiativeId,
    setStep,
    currentWorkflow,
    navigate,
  ])

  const previousStepCallback = useCallback(() => {
    const workflow = currentWorkflow()
    const stepIndex = workflow.indexOf(step)

    if (stepIndex === -1) {
      throw new Error('Workflow error')
    }

    if (stepIndex === 0) {
      return
    }

    if (step === steps.finish && initiative?.isCalendlyScheduled) {
      setStep(workflow[stepIndex - 2])
      return
    }

    const nextStep = workflow[stepIndex - 1]
    setStep(nextStep)
  }, [step, initiative?.isCalendlyScheduled, setStep, currentWorkflow])

  const completeStep = useCallback(
    (step: InitiativeCreationStep) => {
      setCompletedSteps((prev) =>
        prev.has(step) ? prev : new Set(prev).add(step),
      )
    },
    [setCompletedSteps],
  )
  const uncompleteStep = useCallback(
    (step: InitiativeCreationStep) => {
      setCompletedSteps((prev) => {
        const newSet = new Set(prev)
        newSet.delete(step)
        return newSet
      })
    },
    [setCompletedSteps],
  )

  useEffect(() => {
    if (newInitiativeId) {
      client
        .query('get-initiative', {
          id: Number(newInitiativeId),
          userId: authState.userId,
        })
        .subscribe((result) => {
          setInitiative(result)
          setPhase({ id: result.phase.id, type: result.phase.phaseType })
        })
    }
  }, [client, newInitiativeId, authState, setInitiative, setPhase])

  useEffect(() => {
    if (!phase) {
      return
    } else if (!newInitiativeId) {
      client
        .call('create-initiative', { phase: { id: phase.id } })
        .then((result) => {
          if (result) {
            setNewInitiativeId(result)
            completeStep(steps.phaseSelection)
          }
          if (!result) {
            showFailToast()
          }
        })
        .catch(() => {
          showFailToast()
        })
    } else if (step === steps.phaseSelection) {
      client
        .call('edit-initiative', {
          id: Number(newInitiativeId),
          value: { phase: { id: phase.id } },
        })
        .then((success) => {
          if (success) {
            completeStep(steps.phaseSelection)
          } else {
            showFailToast()
          }
        })
        .catch(() => {
          showFailToast()
        })
    }
  }, [
    client,
    phase,
    step,
    newInitiativeId,
    setNewInitiativeId,
    completeStep,
    showFailToast,
  ])

  const nextStepButton: { label: string; icon?: IconDefinition } = match(step)
    .with(steps.contactDetails, () => ({
      label: translations.done,
    }))
    .with(steps.callSchedule, () => ({
      label: isEditing ? translations.skip : translations.closeTheForm,
    }))
    .with(steps.finish, () => ({
      label: translations.closeTheForm,
    }))
    .otherwise(() => ({
      label: translations.next,
      icon: faArrowRight,
    }))

  return (
    <InitiativeCreatePageLayout
      title={translations.createInitiative}
      onClose={() => setExitConfirmationOpen(true)}
    >
      <div className="cq"
      >
        {match(step)
          .with(steps.phaseSelection, () => (
            <PhaseSelectionStep
              selectedPhaseType={phase?.type}
              setSelectedPhase={setPhase}
            />
          ))
          .with(steps.mainDetails, () => (
            <LoaderWrapper loading={!initiative}>
              <MainDetailsStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
              />
            </LoaderWrapper>
          ))
          .with(steps.livingParameters, () => (
            <LoaderWrapper loading={!initiative}>
              <LivingParametersStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
              />
            </LoaderWrapper>
          ))
          .with(steps.membersParameters, () => (
            <LoaderWrapper loading={!initiative}>
              <MembersParametersStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
              />
            </LoaderWrapper>
          ))
          .with(steps.legalInfo, () => (
            <LoaderWrapper loading={!initiative}>
              <LegalInfoStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
              />
            </LoaderWrapper>
          ))
          .with(steps.amenities, () => (
            <LoaderWrapper loading={!initiative}>
              <AmenitiesStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
              />
            </LoaderWrapper>
          ))
          .with(steps.contactDetails, () => (
            <LoaderWrapper loading={!initiative}>
              <ContactDetailsStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
              />
            </LoaderWrapper>
          ))
          .with(steps.callSchedule, () => (
            <LoaderWrapper loading={!initiative}>
              <CallScheduleStep
                initiative={initiative}
                selectedPhaseType={phase?.type}
                completeStep={completeStep}
                uncompleteStep={uncompleteStep}
                isEditing={isEditing}
              />
            </LoaderWrapper>
          ))
          .with(steps.finish, () => (
            <LoaderWrapper loading={!initiative}>
              <FinishStep initiativeId={Number(initiativeId)} />
            </LoaderWrapper>
          ))
          .exhaustive()}
      </div>
      <Row
        alignItems="center"
        style={{
          justifyContent: 'space-between',
          flexDirection: 'row-reverse',
          position: 'fixed',
          width: isMobile ? '100%' : '85%',
          paddingInline: isMobile ? '5rem' : 0,
          maxWidth: '77.5rem',
          bottom: '1.5rem',
        }}
      >
        <Button
          label={nextStepButton.label}
          icon={nextStepButton.icon}
          disabled={!isNextStepEnabled()}
          onClick={nextStepCallback}
        />
        {isPreviousVisible() && (
          <Button
            label={translations.back}
            icon={faArrowLeft}
            iconPosition="left"
            variant="secondary"
            onClick={previousStepCallback}
          />
        )}
      </Row>
      <Modal
        title={translations.closeModalTitle}
        isOpen={exitConfirmationOpen}
        onClose={() => setExitConfirmationOpen(false)}
        onConfirm={() =>
          navigate(
            newInitiativeId
              ? pages.dashboardViewInitiative(newInitiativeId)
              : pages.dashboardInitiatives,
          )
        }
      >
        <Text.Medium style={{ whiteSpace: 'preserve', width: '20rem' }}>
          {translations.closeModalContent}
        </Text.Medium>
      </Modal>
    </InitiativeCreatePageLayout>
  )
}
